import { cn } from '@rocco/utils/cn';

/**
 * Shared detail-page CTA pill — mirrors the design-system button component
 * (Figma 1679:57846 filled / 1679:57849 outlined) EXACTLY:
 *   16px Neue Haas 65 Medium · tracking 0.32px · leading 1.3 · px-26 · 52px tall · pill.
 *
 * Sizes are rem so they hit the Figma px at the 1920/414 design anchors and
 * still scale with browser zoom (see rem-not-vw rule). Compose the per-button
 * colour via `variant`; pass `max-md:w-full` etc. through `extra`.
 */
export type DetailCtaVariant =
    | 'filled-light' // dark pill on a light section (Get in touch)
    | 'outlined-light' // hairline pill on a light section (Visit Website)
    | 'filled-dark' // white pill on a dark section (Book a Meeting)
    | 'outlined-dark' // white-hairline pill on a dark section (Send Enquiry)
    | 'filled-invert' // solid white pill on a dark section (closing prompt CTA)
    | 'grey-dark'; // #2d2d2d pill on a dark section (View all Projects)

const DETAIL_CTA_BASE =
    'inline-flex h-[3.25rem] items-center justify-center gap-[0.375rem] whitespace-nowrap rounded-full px-[1.625rem] font-sans text-base font-medium leading-[1.3] tracking-[0.02em] transition-colors';

const DETAIL_CTA_VARIANTS: Record<DetailCtaVariant, string> = {
    'filled-light': 'bg-[#181818] text-[#f3f3f3] hover:bg-[#2d2d2d]',
    'outlined-light':
        'border border-[#181818] text-[#232323] hover:bg-[#181818] hover:text-[#f3f3f3]',
    'filled-dark':
        'bg-[#333333] text-[#f3f3f3] hover:bg-[#eee]/10 shadow-[0 3.243px 22.703px 0 rgba(0, 0, 0, 0.11)]',
    'outlined-dark':
        'border border-white/60 text-white hover:bg-white hover:text-[#181818]',
    'filled-invert': 'bg-white text-[#181818] hover:bg-white/85',
    'grey-dark': 'bg-[#2d2d2d] text-white hover:bg-[#3a3a3a]',
};

/** The matching 52px square (icon-only) button, e.g. the Save / bookmark pill. */
export const DETAIL_CTA_ICON_SIZE = 'size-[3.25rem]';

export const detailCta = (variant: DetailCtaVariant, extra?: string) =>
    cn(DETAIL_CTA_BASE, DETAIL_CTA_VARIANTS[variant], extra);
